import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  addForumPost,
  getForumPostsByProjectId,
  deleteForumPost,
  addReplyToPost,
  logActivity
} from '../api/apiService';

const getCategoryColor = (category) => {
  switch (category) {
    case 'announcement':
      return '#1976D2'; // Blue
    case 'discussion':
      return '#2E7D32'; // Green
    case 'question':
      return '#ED6C02'; // Orange
    default:
      return '#757575'; // Grey
  }
};

const ForumTab = ({ projectId }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for forum functionality
  const [openForumDialog, setOpenForumDialog] = useState(false);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [forumPost, setForumPost] = useState({
    title: '',
    content: '',
    category: 'general'
  });
  const [replyContent, setReplyContent] = useState('');
  const [forumPosts, setForumPosts] = useState([]);
  const [loadingForum, setLoadingForum] = useState(false);

  // Fetch forum posts when component mounts or projectId changes
  useEffect(() => {
    const fetchForumPosts = async () => {
      if (projectId) {
        try {
          setLoadingForum(true);
          const posts = await getForumPostsByProjectId(projectId);
          setForumPosts(posts);
        } catch (error) {
          console.error('Error fetching forum posts:', error);
        } finally {
          setLoadingForum(false);
        }
      }
    };

    fetchForumPosts();
  }, [projectId]);

  const handleForumPost = async () => {
    try {
      const newPost = {
        title: forumPost.title,
        content: forumPost.content,
        category: forumPost.category,
        author: currentUser?.email || 'Anonymous',
      };

      // Call the service function to add the post
      const createdPost = await addForumPost(projectId, newPost);
      
      // Update the local state with the new post
      setForumPosts([createdPost, ...forumPosts]);
      
      // Reset form and close dialog
      setForumPost({ title: '', content: '', category: 'general' });
      setOpenForumDialog(false);

      // Log the activity
      await logActivity(currentUser.email, `Created a new forum post: ${newPost.title}`);
    } catch (error) {
      console.error('Error creating forum post:', error);
    }
  };

  const handleReply = async () => {
    if (!selectedPost || !replyContent.trim()) return;

    try {
      const reply = {
        content: replyContent,
        author: currentUser?.email || 'Anonymous',
      };

      // Call the service function to add the reply
      const newReply = await addReplyToPost(projectId, selectedPost.id, reply);
      
      // Update the local state
      setForumPosts(forumPosts.map(post => {
        if (post.id === selectedPost.id) {
          return {
            ...post,
            replies: [...(post.replies || []), newReply]
          };
        }
        return post;
      }));

      // Reset form and close dialog
      setReplyContent('');
      setOpenReplyDialog(false);
      setSelectedPost(null);

      // Log the activity
      await logActivity(currentUser.email, `Replied to forum post: ${selectedPost.title}`);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await deleteForumPost(projectId, postId);
      setForumPosts(forumPosts.filter(post => post.id !== postId));
      // Log the activity
      await logActivity(currentUser.email, `Deleted a forum post`);
    } catch (error) {
      console.error('Error deleting forum post:', error);
    }
  };

  return (
    <Box sx={{ paddingTop: '16px' }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenForumDialog(true)}
            startIcon={<Add />}
          >
            New Post
          </Button>
        </Box>
        
        {loadingForum ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
          </Box>
        ) : forumPosts.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
            No posts yet. Be the first to start a discussion!
          </Typography>
        ) : (
          forumPosts.map((post) => (
            <Card key={post.id} sx={{ 
              borderRadius: '12px', 
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.35)' : '0 2px 4px rgba(0,0,0,0.05)',
              backgroundColor: (theme) => theme.palette.background.paper,
              border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
              mb: 2,
              '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 24px rgba(0,0,0,0.45)' : '0 4px 8px rgba(0,0,0,0.1)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: (theme) => theme.palette.primary.main, mr: 2 }}>
                    {post.author[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: (theme) => theme.palette.text.primary }}>
                        {post.title}
                      </Typography>
                      <Chip
                        label={post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(post.category),
                          color: 'white',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: '20px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Posted by {post.author} • {new Date(post.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  {isAdminOrManager && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeletePost(post.id)}
                      sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211,47,47,0.10)' } }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body1" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  mb: 2,
                  color: (theme) => theme.palette.text.primary,
                  lineHeight: 1.6
                }}>
                  {post.content}
                </Typography>
                
                {/* Replies Section */}
                {post.replies && post.replies.length > 0 && (
                  <Box sx={{ 
                    mt: 2, 
                    pl: 2, 
                    borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.02) : '#F8FAFF',
                    borderRadius: '8px',
                    p: 2
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: (theme) => theme.palette.text.secondary,
                      mb: 1,
                      fontWeight: 500
                    }}>
                      {post.replies.length} {post.replies.length === 1 ? 'Reply' : 'Replies'}
                    </Typography>
                    {post.replies.map((reply) => (
                      <Box key={reply.id} sx={{ 
                        mb: 2,
                        '&:last-child': {
                          mb: 0
                        }
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 0.5,
                          backgroundColor: (theme) => theme.palette.background.paper,
                          p: 1,
                          borderRadius: '6px'
                        }}>
                          <Avatar sx={{ 
                            width: 24, 
                            height: 24, 
                            fontSize: '0.875rem', 
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.35), 
                            mr: 1 
                          }}>
                            {reply.author[0].toUpperCase()}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: (theme) => theme.palette.text.primary }}>
                            {reply.author}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                            {new Date(reply.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          pl: 4,
                          color: (theme) => theme.palette.text.secondary,
                          lineHeight: 1.5
                        }}>
                          {reply.content}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
              <Divider />
              <CardActions sx={{ 
                justifyContent: 'flex-end', 
                px: 2, 
                py: 1,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.02) : '#F8FAFF'
              }}>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => {
                    setSelectedPost(post);
                    setOpenReplyDialog(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) }
                  }}
                >
                  Reply
                </Button>
              </CardActions>
            </Card>
          ))
        )}
      </Stack>

      {/* Add Forum Post Dialog */}
      <Dialog
        open={openForumDialog}
        onClose={() => setOpenForumDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Create New Post</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Title"
              value={forumPost.title}
              onChange={(e) => setForumPost({ ...forumPost, title: e.target.value })}
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={forumPost.category}
                label="Category"
                onChange={(e) => setForumPost({ ...forumPost, category: e.target.value })}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="discussion">Discussion</MenuItem>
                <MenuItem value="question">Question</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Content"
              multiline
              rows={4}
              value={forumPost.content}
              onChange={(e) => setForumPost({ ...forumPost, content: e.target.value })}
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForumDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleForumPost}
            variant="contained"
            color="primary"
            disabled={!forumPost.title || !forumPost.content}
          >
            Post
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Reply Dialog */}
      <Dialog
        open={openReplyDialog}
        onClose={() => {
          setOpenReplyDialog(false);
          setSelectedPost(null);
          setReplyContent('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>
          Reply to: {selectedPost?.title}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Reply"
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenReplyDialog(false);
              setSelectedPost(null);
              setReplyContent('');
            }} 
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleReply}
            variant="contained"
            color="primary"
            disabled={!replyContent.trim()}
          >
            Post Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForumTab; 